/**
 * @file SharedArtifactsPage.tsx
 * @description US3 — Shared Artifacts Risk View with KPI cards, filters, table, CSV export
 * @feature 142-governance-compliance-dashboard
 * @constitution v1.12.0
 *
 * Conformed to Spec 197 design blueprint — migrated off the bespoke PageHeader /
 * DualScrollTable / hand-rolled cards, tiles, filters and badges onto the canonical
 * token-driven UI kit. Presentation-only: data, filters, sort, search, CSV export
 * and the underlying query are unchanged.
 */

import React, { useState, useMemo } from 'react';
import { Globe, AlertOctagon, AlertTriangle, Search, Download, ChevronUp, ChevronDown, Database } from 'lucide-react';
import { useSharedArtifacts, downloadCsv } from '../../../services/operations/governanceService';
import type { SharedArtifact } from '../../../types/governance';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';

// ============================================================================
// Types & helpers
// ============================================================================

type SortDir = 'asc' | 'desc';

// ============================================================================
// Main Component
// ============================================================================

const SharedArtifactsPage: React.FC = () => {
  const { data, isLoading, error, refetch } = useSharedArtifacts();

  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('riskLevel');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (field: string) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const artifactTypes = useMemo(() => {
    if (!data?.artifacts) return [];
    return [...new Set(data.artifacts.map((a) => a.artifactType))].sort();
  }, [data?.artifacts]);

  const filtered = useMemo(() => {
    if (!data?.artifacts) return [];
    let list = data.artifacts;

    if (riskFilter !== 'all') list = list.filter((a) => a.riskLevel === riskFilter);
    if (typeFilter !== 'all') list = list.filter((a) => a.artifactType === typeFilter);
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      list = list.filter(
        (a) =>
          (a.artifactName && a.artifactName.toLowerCase().includes(lower)) ||
          a.workspaceName.toLowerCase().includes(lower) ||
          (a.sharedBy && a.sharedBy.toLowerCase().includes(lower))
      );
    }

    return [...list].sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortField] ?? '';
      const bVal = (b as unknown as Record<string, unknown>)[sortField] ?? '';
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data?.artifacts, riskFilter, typeFilter, searchTerm, sortField, sortDir]);

  const handleExportCsv = () => {
    const date = new Date().toISOString().split('T')[0];
    let csv = 'Artifact Name,Type,Sharing Type,Workspace,Shared By,Risk Level\n';
    filtered.forEach((a) => {
      csv += `"${a.artifactName || ''}","${a.artifactType}","${a.sharingType}","${a.workspaceName}","${a.sharedBy || ''}","${a.riskLevel}"\n`;
    });
    downloadCsv(csv, `shared-artifacts-${date}.csv`);
  };

  const isEmpty = !isLoading && (!data?.artifacts || data.artifacts.length === 0);
  const summary = data?.summary;

  // Sortable header: a clickable label + direction caret, used per-column.
  const sortHeader = (label: string, field: string) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="flex items-center gap-1 font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-accent)]"
    >
      {label}
      {sortField === field && (sortDir === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />)}
    </button>
  );

  const columns: DataTableColumn<SharedArtifact>[] = [
    {
      key: 'artifactName',
      header: sortHeader('Artifact Name', 'artifactName'),
      width: '24%',
      render: (a) =>
        a.artifactName ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'artifactType',
      header: sortHeader('Type', 'artifactType'),
      width: '14%',
      render: (a) => <span className="text-[var(--color-text-secondary)]">{a.artifactType}</span>,
    },
    {
      key: 'sharingType',
      header: sortHeader('Sharing', 'sharingType'),
      width: '14%',
      render: (a) => (
        <span className="text-[var(--color-text-secondary)]">
          {a.sharingType === 'PublishedToWeb' ? 'Published' : 'Shared Org'}
        </span>
      ),
    },
    {
      key: 'workspaceName',
      header: sortHeader('Workspace', 'workspaceName'),
      width: '20%',
      render: (a) => <span className="text-[var(--color-text-secondary)]">{a.workspaceName}</span>,
    },
    {
      key: 'sharedBy',
      header: sortHeader('Shared By', 'sharedBy'),
      width: '16%',
      render: (a) =>
        a.sharedBy ?? <span className="text-[var(--color-text-secondary)]">—</span>,
    },
    {
      key: 'riskLevel',
      header: sortHeader('Risk', 'riskLevel'),
      width: '12%',
      render: (a) => (
        <Badge variant={a.riskLevel === 'Critical' ? 'danger' : 'warning'}>
          {a.riskLevel}
        </Badge>
      ),
    },
  ];

  return (
    <PageLayout
      title="Shared Artifacts"
      description="Artifacts published to web or shared organization-wide"
      icon={<Globe className="h-6 w-6" />}
            helpId="governance.shared-artifacts"
            onRefresh={() => { void refetch(); }}
            isRefreshing={isLoading}
            actions={
        <>
          <Button leftIcon={<Download className="h-4 w-4" />} onClick={handleExportCsv} disabled={isEmpty}>
            Export CSV
          </Button>
        </>
      }
    >
      {/* KPI tiles */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Total Shared"
          value={isEmpty ? '—' : summary?.totalShared ?? '—'}
          icon={<Globe className="h-4 w-4" />}
        />
        <StatCard
          label="Published to Web"
          value={isEmpty ? '—' : summary?.publishedToWebCount ?? '—'}
          icon={<AlertOctagon className="h-4 w-4" />}
          valueClassName={(summary?.publishedToWebCount ?? 0) > 0 ? 'text-[var(--color-status-danger)]' : undefined}
        />
        <StatCard
          label="Shared to Org"
          value={isEmpty ? '—' : summary?.sharedToOrgCount ?? '—'}
          icon={<AlertTriangle className="h-4 w-4" />}
        />
      </div>

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      {isLoading ? (
        <Card padding="none">
          <div className="p-4">
            <LoadingState rows={6} />
          </div>
        </Card>
      ) : error ? (
        <ErrorState
          title="Failed to load shared artifacts"
          message={error instanceof Error ? error.message : 'Unknown error'}
          onRetry={() => refetch()}
        />
      ) : isEmpty ? (
        <EmptyState
          icon={<Database className="h-10 w-10" />}
          title="No governance data collected yet"
          message="Governance collection requires Fabric Admin API access. Contact your admin to enable the governance collector."
        />
      ) : (
        <Extended>
        <Card padding="none">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 border-b border-[var(--color-border)] p-3">
            <div className="min-w-[200px] max-w-xs flex-1">
              <Input
                isFullWidth
                placeholder="Search artifacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftAddon={<Search className="h-4 w-4" />}
              />
            </div>
            <Select
              value={riskFilter}
              onChange={(e) => setRiskFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Risk' },
                { value: 'Critical', label: 'Critical' },
                { value: 'Warning', label: 'Warning' },
              ]}
            />
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Types' },
                ...artifactTypes.map((t) => ({ value: t, label: t })),
              ]}
            />
            <span className="ml-auto text-xs text-[var(--color-text-secondary)]">
              Showing {filtered.length} of {data?.artifacts?.length ?? 0}
            </span>
          </div>

          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(a, idx) => `${a.artifactName}-${a.sharingType}-${idx}`}
            emptyMessage="No artifacts match the current filters"
            minWidth="52rem"
          />
        </Card>
        </Extended>
      )}
    </PageLayout>
  );
};

export default SharedArtifactsPage;
